import React, { FC, useMemo, useState } from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import ReactionPicker from './ReactionPicker';
import { useAuthSheet } from './Mobile/AuthSheetContext';
import { REACTION_DEFINITIONS } from '../constants/reactions';
import type { ReactionMap, ReactionEntry, UserState } from '../types/common';

interface ReactionBarProps {
  user: UserState | null;
  reactions: ReactionMap;
  /** Per-user reaction state, used to resolve which keys the current user holds. */
  reactionEntries?: ReactionEntry[];
  /**
   * Add or remove a reaction for the current user. `held` says whether they
   * already have it, so the parent can call the right idempotent endpoint.
   */
  onToggle: (reactionKey: string, held: boolean) => void;
  size?: 'small' | 'medium';
}

/**
 * One chip per used reaction (highest count first). A user may hold as many
 * different reactions on an item as they like and remove any of them at any
 * time — nothing locks, because the author's Point is granted once per
 * (reactor, item) server-side and is never clawed back or re-earned.
 */
const ReactionBar: FC<ReactionBarProps> = ({ user, reactions, reactionEntries, onToggle, size = 'medium' }) => {
  const theme = useTheme();
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const openAuthSheet = useAuthSheet();

  const handleToggle = (reactionKey: string, held: boolean) => {
    if (!user) {
      // Mobile app: prompt login instead of a dead tap. Web reaches this only
      // via disabled controls, so the no-op stays.
      openAuthSheet?.();
      return;
    }
    onToggle(reactionKey, held);
  };

  // Used reactions with counts, sorted by count desc (registry order breaks ties).
  const active = useMemo(
    () =>
      REACTION_DEFINITIONS.map(def => ({ def, count: reactions[def.key]?.length ?? 0 }))
        .filter(item => item.count > 0)
        .sort((a, b) => b.count - a.count || a.def.order - b.def.order),
    [reactions]
  );

  const chipHeight = size === 'small' ? 26 : 30;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flexWrap: 'wrap' }}>
      {active.map(item => {
        const reacted = Boolean(user && reactions[item.def.key]?.includes(user.id));
        return (
          <Tooltip key={item.def.key} title={item.def.label}>
            <ButtonBase
              onClick={() => handleToggle(item.def.key, reacted)}
              disabled={!user && !openAuthSheet}
              aria-pressed={reacted}
              aria-label={`${item.def.label}, ${item.count}${reacted ? ', reacted' : ''}`}
              sx={{
                height: chipHeight,
                pl: 0.75,
                pr: 1,
                gap: 0.5,
                borderRadius: 'var(--radius-pill)',
                backgroundColor: reacted
                  ? theme.tokens.colors.redSoft
                  : theme.tokens.colors.backgroundCardHover,
                border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${
                  reacted ? theme.tokens.colors.redLine : theme.tokens.colors.cardBorder
                }`,
                transition: `border-color ${theme.tokens.animations.fast} ${theme.tokens.animations.easing}`,
                '&:hover': { borderColor: theme.tokens.colors.glassLine2 },
              }}
            >
              <Box component="span" sx={{ fontSize: size === 'small' ? 14 : 16, lineHeight: 1 }}>
                {item.def.emoji}
              </Box>
              <Typography
                component="span"
                sx={{
                  fontFamily: theme.tokens.typography.monoFontFamily,
                  fontWeight: theme.tokens.typography.fontWeightMedium,
                  fontSize: size === 'small' ? 12 : 13,
                  color: theme.tokens.colors.text2,
                  lineHeight: 1,
                }}
              >
                {item.count}
              </Typography>
            </ButtonBase>
          </Tooltip>
        );
      })}

      {(user || openAuthSheet) && (
        <>
          <Tooltip title="Add reaction">
            <IconButton
              size="small"
              onClick={event =>
                user ? setPickerAnchor(event.currentTarget) : openAuthSheet?.()
              }
              aria-label="Add reaction"
              sx={{
                color: theme.tokens.colors.text3,
                '&:hover': { color: theme.tokens.colors.textPrimary },
              }}
            >
              <AddReactionOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {user && (
            <ReactionPicker
              anchorEl={pickerAnchor}
              onClose={() => setPickerAnchor(null)}
              user={user}
              reactions={reactions}
              onSelect={handleToggle}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ReactionBar;
